import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import {
  Home, ShoppingBag, Users, BarChart3, Settings, Wrench,
  CreditCard, UserCog, Award, Wallet, LogOut, Plus, Menu, X, Shirt, ArrowLeft, Percent, Building2, Calculator,
} from "lucide-react";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/", label: "Accueil", icon: Home, roles: ["admin", "agent"] },
  { to: "/orders", label: "Commandes", icon: ShoppingBag, roles: ["admin", "agent"] },
  { to: "/customers", label: "Clients", icon: Users, roles: ["admin", "agent"] },
  { to: "/pro", label: "Professionnels", icon: Building2, roles: ["admin", "agent"] },
  { to: "/cash", label: "Caisse", icon: Wallet, roles: ["admin", "agent"] },
  { to: "/comptabilite", label: "Comptabilité", icon: Calculator, roles: ["admin"] },
  { to: "/reports", label: "Rapports", icon: BarChart3, roles: ["admin"] },
  { to: "/services", label: "Services", icon: Wrench, roles: ["admin"] },
  { to: "/payment-methods", label: "Paiements", icon: CreditCard, roles: ["admin"] },
  { to: "/agents", label: "Agents", icon: UserCog, roles: ["admin"] },
  { to: "/loyalty", label: "Fidélité", icon: Award, roles: ["admin"] },
  { to: "/promotions", label: "Promotions", icon: Percent, roles: ["admin"] },
  { to: "/settings", label: "Réglages", icon: Settings, roles: ["admin"] },
];

export default function Layout({ children }) {
  const { user, logout, settings } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const [rewardsCount, setRewardsCount] = useState(0);
  const role = user?.role || "agent";
  const items = NAV.filter((i) => i.roles.includes(role));
  const company = settings?.company_name || "WuraPro";

  useEffect(() => {
    let alive = true;
    const fetchCount = () => api.get("/loyalty/rewards-due").then((r) => { if (alive) setRewardsCount(r.data.length); }).catch(() => {});
    fetchCount();
    const t = setInterval(fetchCount, 60000);
    return () => { alive = false; clearInterval(t); };
  }, [loc.pathname]);

  const Logo = ({ onClick, testid }) => (
    <button data-testid={testid} onClick={onClick} className="flex items-center gap-2.5">
      {settings?.logo_url ? (
        <img src={settings.logo_url} alt="logo" className="h-9 w-9 rounded-lg object-cover" />
      ) : (
        <div className="h-9 w-9 rounded-lg bg-sky-500 text-white grid place-items-center shadow-lg shadow-sky-500/30">
          <Shirt size={20} />
        </div>
      )}
      <span className="font-display font-extrabold text-lg tracking-tight">{company}</span>
    </button>
  );

  const Item = ({ i, dark }) => {
    const active = loc.pathname === i.to || (i.to !== "/" && loc.pathname.startsWith(i.to));
    const badge = i.to === "/" && rewardsCount > 0 ? rewardsCount : null;
    return (
      <Link
        to={i.to}
        data-testid={`nav-${i.label.toLowerCase()}`}
        onClick={() => setOpen(false)}
        className={`flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm font-medium transition-colors ${
          active
            ? "bg-sky-500 text-white shadow-md shadow-sky-500/20"
            : dark ? "text-slate-300 hover:bg-white/10 hover:text-white" : "text-slate-600 hover:bg-slate-100"
        }`}
      >
        <i.icon size={18} /> <span className="flex-1">{i.label}</span>
        {badge && <span data-testid="nav-rewards-badge" className="ml-auto text-[11px] font-bold bg-amber-400 text-amber-950 rounded-full min-w-5 h-5 px-1.5 grid place-items-center">{badge}</span>}
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Sidebar desktop - dark */}
      <aside className="hidden md:flex md:flex-col w-64 bg-slate-900 text-white p-4 gap-1 sticky top-0 h-screen">
        <div className="px-2 py-2 mb-4 text-white">
          <Logo testid="logo-home-btn" onClick={() => nav("/")} />
        </div>
        <nav className="flex flex-col gap-1 overflow-y-auto flex-1">
          {items.map((i) => <Item key={i.to} i={i} dark />)}
        </nav>
        <div className="mt-auto pt-4 border-t border-white/10">
          <div className="px-2 mb-2 flex items-center gap-2">
            <div className="h-8 w-8 rounded-full bg-sky-500/20 text-sky-300 grid place-items-center font-semibold text-sm">
              {(user?.name || "?").charAt(0).toUpperCase()}
            </div>
            <div className="text-xs">
              <p className="font-medium text-white leading-tight">{user?.name}</p>
              <p className="text-slate-400 capitalize">{role}</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" data-testid="logout-btn" onClick={() => { logout(); nav("/login"); }} className="w-full justify-start text-rose-300 hover:text-rose-200 hover:bg-white/10">
            <LogOut size={16} className="mr-2" /> Déconnexion
          </Button>
        </div>
      </aside>

      {/* Mobile */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="md:hidden flex items-center justify-between bg-slate-900 text-white px-4 py-3 sticky top-0 z-20">
          <Logo testid="logo-home-btn-mobile" onClick={() => nav("/")} />
          <button data-testid="menu-toggle" onClick={() => setOpen(!open)}>
            {open ? <X /> : <Menu />}
          </button>
        </header>
        {open && (
          <div className="md:hidden bg-slate-900 p-3 flex flex-col gap-1 z-20">
            {items.map((i) => <Item key={i.to} i={i} dark />)}
            <Button variant="ghost" size="sm" onClick={() => { logout(); nav("/login"); }} className="justify-start text-rose-300 hover:bg-white/10">
              <LogOut size={16} className="mr-2" /> Déconnexion
            </Button>
          </div>
        )}

        <main className="flex-1 p-5 md:p-8 pb-24 md:pb-8 max-w-[1400px] w-full mx-auto">
          {loc.pathname !== "/" && (
            <button
              data-testid="back-btn"
              onClick={() => nav(-1)}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-800 mb-4 transition-colors"
            >
              <ArrowLeft size={18} /> Retour
            </button>
          )}
          {children}
        </main>

        {/* Bottom nav mobile */}
        <nav className="md:hidden fixed bottom-0 inset-x-0 bg-white border-t border-slate-200 flex justify-around py-2 z-20">
          {[NAV[0], NAV[1], { to: "/orders/new", label: "Nouveau", icon: Plus }, NAV[2], NAV[3]].map((i) => {
            const active = loc.pathname === i.to;
            return (
              <Link key={i.label} to={i.to} data-testid={`bottomnav-${i.label.toLowerCase()}`} className={`flex flex-col items-center text-[11px] px-2 ${active ? "text-sky-600" : "text-slate-500"}`}>
                <i.icon size={20} /> {i.label}
              </Link>
            );
          })}
        </nav>
      </div>
    </div>
  );
}
